import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HUMAN_DATE_FORMAT } from '../../utils/constants';
import { nextBillingDate, pauseSubscription } from '../api';
import Modal from '../../components/Modal';
import { useGlobalContext } from '../state/GlobalContext';

interface IProps {
  subscription: Subscription;
  onUpdate: (res: Subscription, msg: string) => void;
  onError: (msg: string) => void;
  setOpen: (value: boolean) => void;
  open: boolean;
}

enum Options {
  ONE_MONTH = '1 month',
  TWO_MONTH = '2 months',
  THREE_MONTH = '3 months',
  CUSTOM = 'Custom',
  INDEFINITELY = 'Indefinitely',
}

export default ({
  open,
  setOpen,
  subscription,
  onUpdate,
  onError,
}: IProps): React.ReactElement => {
  const nextBilling = useMemo(
    () => dayjs(subscription.NextBillingDate),
    [subscription],
  );

  const { t } = useTranslation();
  const { customerId, subscriptionId, authToken } = useGlobalContext();
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Options>(
    Options.ONE_MONTH,
  );
  const [datePickerValue, setDatePickerValue] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs>(() =>
    nextBilling.add(1, 'month'),
  );
  const closeModal = () => {
    setSelectedOption(Options.ONE_MONTH);
    setDatePickerValue('');
    setSelectedDate(nextBilling.add(1, 'month'));
    setOpen(false);
  };

  useEffect(() => {
    switch (selectedOption) {
      case Options.ONE_MONTH:
        setSelectedDate(nextBilling.add(1, 'month'));
        break;
      case Options.TWO_MONTH:
        setSelectedDate(nextBilling.add(2, 'month'));
        break;
      case Options.THREE_MONTH:
        setSelectedDate(nextBilling.add(3, 'month'));
        break;
    }
  }, [selectedOption, subscription, nextBilling]);

  const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOption(Options[e.target.value]);
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDatePickerValue(e.target.value);
    setSelectedDate(dayjs(e.target.value));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (selectedOption === Options.INDEFINITELY) {
        const res = await pauseSubscription(
          customerId,
          subscriptionId,
          authToken,
        );

        onUpdate(res, t('subscription.pause_success'));
      } else {
        const res = await nextBillingDate(
          selectedDate,
          customerId,
          subscriptionId,
          authToken,
          'UPDATED_ORDER_DATE',
        );

        onUpdate(
          res,
          t('subscription.skip_billing_interval_success', {
            date: selectedDate.format(HUMAN_DATE_FORMAT),
          }),
        );
      }
    } catch (err) {
      console.error(err);
      onError(t('subscription.skip_billing_interval_error'));
    } finally {
      setLoading(false);
      closeModal();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={closeModal}
        title={t('subscription.pause_subscription')}
      >
        <div>
          <div>
            <form
              style={{ marginBottom: 100 }}
              onSubmit={handleFormSubmit}
              id="bundle_pause_form"
            >
              <div className="bundleapp-flex" style={{ marginBottom: 20 }}>
                <div>
                  <select
                    style={{ width: 200 }}
                    value={Options[selectedOption]}
                    onChange={handleOptionChange}
                  >
                    {Object.keys(Options).map((key) => (
                      <option key={key} value={key}>
                        {Options[key]}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedOption === Options.CUSTOM && (
                  <div style={{ marginLeft: 20 }}>
                    <input
                      style={{ width: 200 }}
                      type="date"
                      required
                      value={datePickerValue}
                      min={nextBilling.format('YYYY-MM-DD')}
                      onChange={handleDatePickerChange}
                    />
                  </div>
                )}
              </div>
              <p>
                {selectedOption === Options.INDEFINITELY
                  ? t('subscription.pause_disclaimer_indefinitely')
                  : t('subscription.pause_disclaimer_date', {
                      date: selectedDate.format(HUMAN_DATE_FORMAT),
                    })}
              </p>
            </form>
          </div>
          <div className="bundleapp-modal__footer">
            <button
              type="button"
              onClick={closeModal}
              className="btn btn--secondary-accent"
              style={{ marginRight: 10 }}
            >
              {t('titles.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`btn btn-primary ${loading && 'bundleapp-loading'}`}
              form="bundle_pause_form"
            >
              {t('titles.save')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
